'use client';

import { useState, type CSSProperties } from 'react';
import { Sparkle } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ContinueFlowSuggestionsProps {
  suggestions: string[];
  disabled?: boolean;
  onPick: (suggestion: string) => void;
  /** Overrides the default top spacing for callers that space the block themselves. */
  className?: string;
}

/** "Continue the flow" chips from SSE `suggestions` events. */
export function ContinueFlowSuggestions({
  suggestions,
  disabled = false,
  onPick,
  className,
}: ContinueFlowSuggestionsProps) {
  const [selected, setSelected] = useState<string | null>(null);
  const isLocked = disabled || selected != null;

  if (suggestions.length === 0) return null;

  return (
    <div
      className={cn(
        "command-flow-block mt-4 flex w-full min-w-0 flex-col gap-2.5",
        className
      )}
    >
      <div className="command-flow-label flex items-center gap-1.5">
        <Sparkle
          className="size-3.5 shrink-0 fill-primary text-primary"
          aria-hidden
        />
        <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Continue the flow
        </span>
      </div>

      <div className="flex w-full min-w-0 flex-wrap gap-2">
        {suggestions.map((suggestion, index) => {
          const isSelected = selected === suggestion;

          return (
            <button
              key={suggestion}
              type="button"
              disabled={isLocked}
              onClick={() => {
                if (isLocked) return;
                setSelected(suggestion);
                onPick(suggestion);
              }}
              style={{ "--flow-i": index } as CSSProperties}
              className={cn(
                'command-flow-chip inline-flex max-w-full items-center gap-1.5 rounded-full border bg-background px-3.5 py-2 text-left text-sm text-foreground transition-colors',
                'border-border hover:border-primary/40 hover:bg-muted/40',
                'disabled:cursor-default disabled:opacity-55 disabled:hover:border-border disabled:hover:bg-background',
                isSelected && 'border-primary/50 bg-primary/5 disabled:opacity-100',
              )}
            >
              <Sparkle
                className="size-3.5 shrink-0 text-foreground"
                strokeWidth={1.75}
                aria-hidden
              />
              <span className="min-w-0 truncate font-medium leading-none">
                {suggestion}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
